import { Link } from "react-router-dom";
import { ArrowUpRight, ClipboardCheck, MapPin, Search, PhoneCall } from "lucide-react";
import SiteLayout from "@/components/site/SiteLayout";
import JsonLd from "@/components/site/JsonLd";
import { CS_TOWNS } from "@/lib/conditionSurveys";
import { BRAND } from "@/lib/content";

const STEPS = [
  { icon: Search, title: "Thorough inspection", text: "Two to three hours on site — roof space included — examining structure, roof, walls, damp, services, joinery and drainage." },
  { icon: ClipboardCheck, title: "Traffic-light report", text: "Every element rated green, amber or red, photographed, and explained in plain English within 3–5 working days." },
  { icon: PhoneCall, title: "Walkthrough call", text: "A follow-up call to go through the findings — what needs attention now, what can wait, and what's nothing to worry about." },
];

export default function CsHub() {
  const schema = {
    "@context": "https://schema.org",
    "@type": "Service",
    name: "Property Condition Surveys in Lincolnshire",
    serviceType: "Property Condition Survey",
    provider: {
      "@type": "ProfessionalService",
      name: "Declan Bishop — Property Consultant",
      url: "https://dbpropertyconsultant.co.uk",
      telephone: "+44 7947 981 727",
    },
    areaServed: CS_TOWNS.map((t) => ({ "@type": "City", name: t.name })),
    url: "https://dbpropertyconsultant.co.uk/condition-surveys",
  };

  return (
    <SiteLayout
      pageTitle="Property Condition Surveys — Independent & RICS-Aligned"
      pageDescription="Independent property condition surveys across Lincolnshire — traffic-light reports on roof, walls, damp, structure and services, in plain English. Horncastle, Woodhall Spa, Coningsby, Boston, Lincoln, Louth and beyond."
      pagePath="/condition-surveys"
    >
      <JsonLd id="cs-hub-schema" data={schema} />

      <section data-testid="cs-hero" className="bg-brand-cream border-b border-brand-navy/10">
        <div className="container-narrow pt-16 pb-16 md:pt-24 md:pb-20">
          <div className="overline" data-testid="cs-overline">Property Condition Surveys · Lincolnshire</div>
          <h1
            data-testid="cs-headline"
            className="mt-6 font-serif text-[44px] sm:text-6xl lg:text-7xl text-brand-navy leading-[0.98] tracking-tight max-w-4xl"
          >
            Know exactly what you&apos;re buying —{" "}
            <span className="italic font-light text-brand-champagne">before you commit.</span>
          </h1>
          <p className="mt-8 max-w-2xl text-base md:text-lg text-brand-navy/70 leading-relaxed">
            An independent, RICS-aligned condition survey examines every major element of the
            property — roof, walls, damp, structure, services — and reports in plain English
            with a clear traffic-light rating. No ties to agents, developers or contractors.
          </p>
          <div className="mt-10 flex flex-wrap gap-4">
            <Link
              to="/contact"
              data-testid="cs-cta-primary"
              className="inline-flex items-center gap-3 bg-brand-navy text-brand-cream px-7 py-4 text-[12px] tracking-[0.22em] uppercase font-semibold hover:bg-brand-navy-soft transition-colors"
            >
              Get a Fixed Quote
              <ArrowUpRight size={16} />
            </Link>
            <a
              href={`tel:${BRAND.phone.replace(/\s+/g, "")}`}
              data-testid="cs-cta-call"
              className="inline-flex items-center gap-3 border border-brand-navy/30 text-brand-navy px-7 py-4 text-[12px] tracking-[0.22em] uppercase font-semibold hover:bg-brand-navy hover:text-brand-cream transition-colors"
            >
              Call {BRAND.phone}
            </a>
          </div>
        </div>
      </section>

      <section data-testid="cs-steps" className="bg-white border-b border-brand-navy/10">
        <div className="container-narrow py-20 md:py-28">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-brand-navy/10 border border-brand-navy/10">
            {STEPS.map((s) => (
              <div key={s.title} className="bg-white p-8 md:p-10">
                <s.icon size={22} className="text-brand-champagne" />
                <h2 className="mt-5 font-serif text-2xl text-brand-navy">{s.title}</h2>
                <p className="mt-3 text-sm text-brand-navy/65 leading-relaxed">{s.text}</p>
              </div>
            ))}
          </div>
          <p className="mt-10 text-sm text-brand-navy/60 max-w-3xl leading-relaxed">
            Buying a new build instead? You want a{" "}
            <Link to="/services#new-build-snagging-surveys" className="ink-link font-medium text-brand-navy" data-testid="cs-snagging-link">
              snagging survey
            </Link>{" "}
            — for a formal RICS report, see{" "}
            <Link to="/home-surveys" className="ink-link font-medium text-brand-navy" data-testid="cs-hs-link">
              RICS Home Surveys (Levels 1–3)
            </Link>
            , and if you only need the energy certificate, see{" "}
            <Link to="/epc" className="ink-link font-medium text-brand-navy" data-testid="cs-epc-link">
              EPCs from £85
            </Link>.
          </p>
        </div>
      </section>

      <section data-testid="cs-towns" className="bg-brand-cream">
        <div className="container-narrow py-20 md:py-28">
          <div className="overline">Condition surveys near you</div>
          <h2 className="mt-6 font-serif text-3xl md:text-4xl text-brand-navy tracking-tight max-w-2xl">
            Local survey guides for your town.
          </h2>
          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px bg-brand-navy/10 border border-brand-navy/10">
            {CS_TOWNS.map((t) => (
              <Link
                key={t.slug}
                to={`/condition-surveys/${t.slug}`}
                data-testid={`cs-town-card-${t.slug}`}
                className="group bg-brand-cream p-8 md:p-10 hover:bg-white transition-colors"
              >
                <MapPin size={18} className="text-brand-champagne" />
                <h3 className="mt-5 font-serif text-2xl md:text-3xl text-brand-navy tracking-tight">
                  Condition surveys in {t.name}
                </h3>
                <p className="mt-3 text-sm text-brand-navy/65 leading-relaxed line-clamp-3">{t.intro}</p>
                <span className="mt-6 inline-flex items-center gap-2 text-[11px] tracking-[0.22em] uppercase font-semibold text-brand-navy ink-link">
                  Local guide
                  <ArrowUpRight size={13} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </span>
              </Link>
            ))}
          </div>
          <p className="mt-10 text-sm text-brand-navy/60">
            Surveys are available throughout Lincolnshire —{" "}
            <Link to="/areas" className="ink-link font-medium text-brand-navy" data-testid="cs-all-areas-link">
              see every town covered
            </Link>.
          </p>
        </div>
      </section>
    </SiteLayout>
  );
}
